/**
 * 项目权限 Context
 * 提供项目权限的缓存和共享机制
 */
import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { checkProjectPermissionsBatch } from '@/api/project-permissions';
import { createLogger } from '@/utils/logger';

const logger = createLogger('ProjectPermissionsContext');

interface PermissionCache {
  [projectId: string]: {
    permissions: Record<string, boolean>;
    timestamp: number;
  };
}

type InFlightPermissionMap = Record<string, Promise<Record<string, boolean>>>;
type InFlightBatchPermissionMap = Record<
  string,
  Promise<Record<string, Record<string, boolean>>>
>;

function normalizeStringList(values: string[]): string[] {
  return Array.from(new Set(values.filter(Boolean))).sort();
}

interface ProjectPermissionsContextValue {
  checkPermissions: (projectId: string, permissionCodes: string[]) => Promise<Record<string, boolean>>;
  checkPermissionsBatch: (
    projectIds: string[],
    permissionCodes: string[]
  ) => Promise<Record<string, Record<string, boolean>>>;
  clearCache: (projectId?: string) => void;
}

const ProjectPermissionsContext = createContext<ProjectPermissionsContextValue | undefined>(undefined);

const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

export function ProjectPermissionsProvider({ children }: { children: React.ReactNode }) {
  const cacheRef = useRef<PermissionCache>({});
  const inFlightRef = useRef<InFlightPermissionMap>({});
  const inFlightBatchRef = useRef<InFlightBatchPermissionMap>({});

  /**
   * 批量检查权限（带缓存）
   */
  const checkPermissionsBatch = useCallback(async (
    projectIds: string[],
    permissionCodes: string[]
  ): Promise<Record<string, Record<string, boolean>>> => {
    const normalizedProjectIds = normalizeStringList(projectIds);
    const normalizedPermissionCodes = normalizeStringList(permissionCodes);

    if (normalizedProjectIds.length === 0 || normalizedPermissionCodes.length === 0) {
      return {};
    }

    const now = Date.now();
    const projectIdsNeedFetch: string[] = [];

    normalizedProjectIds.forEach((projectId) => {
      const cached = cacheRef.current[projectId];
      const cacheValid = !!cached && (now - cached.timestamp) < CACHE_DURATION;
      const cachedPermissions = cacheValid ? cached.permissions : {};
      const hasAllPermissions = normalizedPermissionCodes.every(
        (code) => code in cachedPermissions
      );

      if (!hasAllPermissions) {
        projectIdsNeedFetch.push(projectId);
      }
    });

    if (projectIdsNeedFetch.length > 0) {
      const requestKey = `batch::${projectIdsNeedFetch.join('|')}::${normalizedPermissionCodes.join('|')}`;
      let inFlightBatchRequest = inFlightBatchRef.current[requestKey];

      if (!inFlightBatchRequest) {
        inFlightBatchRequest = checkProjectPermissionsBatch(
          projectIdsNeedFetch,
          normalizedPermissionCodes
        )
          .then((permissionsMap) => {
            const timestamp = Date.now();

            projectIdsNeedFetch.forEach((projectId) => {
              const latest = cacheRef.current[projectId];
              cacheRef.current[projectId] = {
                permissions: {
                  ...(latest?.permissions || {}),
                  ...(permissionsMap[projectId] || {}),
                },
                timestamp,
              };
            });

            return permissionsMap;
          })
          .finally(() => {
            delete inFlightBatchRef.current[requestKey];
          });

        inFlightBatchRef.current[requestKey] = inFlightBatchRequest;
      }

      await inFlightBatchRequest;
    }

    const result: Record<string, Record<string, boolean>> = {};
    normalizedProjectIds.forEach((projectId) => {
      const latestPermissions = cacheRef.current[projectId]?.permissions || {};
      result[projectId] = {};
      normalizedPermissionCodes.forEach((code) => {
        result[projectId][code] = latestPermissions[code] || false;
      });
    });

    return result;
  }, []);

  /**
   * 检查权限（带缓存）
   */
  const checkPermissions = useCallback(async (
    projectId: string,
    permissionCodes: string[]
  ): Promise<Record<string, boolean>> => {
    if (!projectId || permissionCodes.length === 0) {
      return {};
    }

    const normalizedPermissionCodes = normalizeStringList(permissionCodes);
    if (normalizedPermissionCodes.length === 0) {
      return {};
    }

    const requestKey = `${projectId}::${normalizedPermissionCodes.join('|')}`;
    let inFlightRequest = inFlightRef.current[requestKey];

    if (!inFlightRequest) {
      inFlightRequest = checkPermissionsBatch([projectId], normalizedPermissionCodes)
        .then((batchResult) => batchResult[projectId] || {})
        .finally(() => {
          delete inFlightRef.current[requestKey];
        });

      inFlightRef.current[requestKey] = inFlightRequest;
    }

    return inFlightRequest;
  }, [checkPermissionsBatch]);

  /**
   * 清除缓存
   */
  const clearCache = useCallback((projectId?: string) => {
    if (projectId) {
      delete cacheRef.current[projectId];
      Object.keys(inFlightRef.current).forEach((key) => {
        if (key.startsWith(`${projectId}::`)) {
          delete inFlightRef.current[key];
        }
      });
      Object.keys(inFlightBatchRef.current).forEach((key) => {
        if (key.includes(projectId)) {
          delete inFlightBatchRef.current[key];
        }
      });
      logger.debug('清除项目权限缓存', { projectId });
    } else {
      cacheRef.current = {};
      inFlightRef.current = {};
      inFlightBatchRef.current = {};
      logger.debug('清除所有项目权限缓存');
    }
  }, []);

  const contextValue = useMemo(
    () => ({ checkPermissions, checkPermissionsBatch, clearCache }),
    [checkPermissions, checkPermissionsBatch, clearCache]
  );

  return (
    <ProjectPermissionsContext.Provider value={contextValue}>
      {children}
    </ProjectPermissionsContext.Provider>
  );
}

/**
 * 使用项目权限 Hook
 */
export function useProjectPermissions() {
  const context = useContext(ProjectPermissionsContext);
  if (!context) {
    throw new Error('useProjectPermissions must be used within ProjectPermissionsProvider');
  }
  return context;
}
